import React, { useEffect, useState } from 'react';
import { useSite } from '../../context/SiteContext';
import { resolveMediaUrl } from '../admin/MediaUpload';

/**
 * Fast 1-second reloading splash with brand logo and gold glowing spinner ring.
 * Takes exactly 1 second for fastest instant page opening.
 */
export default function CricketLoader() {
  const [visible, setVisible] = useState(true);
  const [fading, setFading] = useState(false);
  const { data } = useSite();
  const branding = data?.branding || {};

  useEffect(() => {
    // Start smooth fade-out at 850ms, unmount at exactly 1000ms (1 second)
    const fadeTimer = setTimeout(() => setFading(true), 850);
    const endTimer = setTimeout(() => setVisible(false), 1000);
    return () => {
      clearTimeout(fadeTimer);
      clearTimeout(endTimer);
    };
  }, []);

  if (!visible) return null;

  return (
    <div
      className={`fixed inset-0 z-[200] flex items-center justify-center transition-opacity duration-150 ${
        fading ? 'opacity-0 pointer-events-none' : 'opacity-100'
      }`}
      style={{ background: '#0a0a0a' }}
      data-testid="cricket-loader"
    >
      {/* Soft radial vignette so the spinner pops */}
      <div className="absolute inset-0" style={{ background: 'radial-gradient(ellipse at center, rgba(204,199,125,0.12) 0%, transparent 65%)' }} />

      <div className="relative flex flex-col items-center gap-4">
        {/* Animated ring surrounding the logo */}
        <div className="relative w-28 h-28 flex items-center justify-center">
          {/* Outer track */}
          <div className="absolute inset-0 rounded-full border-2 border-zinc-800/80" />
          
          {/* Fast spinning gold arc */}
          <div
            className="absolute inset-0 rounded-full border-2 border-transparent"
            style={{
              borderTopColor: '#ccc77d',
              borderRightColor: '#ccc77d',
              animation: 'cl-spin 0.8s linear infinite',
            }}
          />

          {/* Logo in Center */}
          <div className="relative z-10 flex items-center justify-center p-2">
            {branding.logoImage ? (
              <img
                src={resolveMediaUrl(branding.logoImage)}
                alt={branding.logoText || 'Logo'}
                className="max-h-12 max-w-[84px] object-contain animate-pulse"
              />
            ) : (
              <div className="flex items-center font-display font-black text-xl tracking-tight leading-none text-white select-none">
                <span>{branding.logoText || 'REDDY'}</span>
                <span className="text-[#ccc77d] ml-1">{branding.logoAccent || 'ANNA'}</span>
              </div>
            )}
          </div>
        </div>

        {/* Loading Indicator dots */}
        <div className="text-center">
          <div className="text-[#ccc77d] font-black tracking-[0.25em] text-[11px] uppercase">
            {branding.tagline || 'OFFICIAL GAMING & SPORTS EXCHANGE'}
          </div>
          <div className="mt-2 flex justify-center gap-1.5">
            <span className="w-1.5 h-1.5 rounded-full bg-[#ccc77d]" style={{ animation: 'cl-bounce 0.8s ease-in-out 0s infinite' }} />
            <span className="w-1.5 h-1.5 rounded-full bg-[#ccc77d]" style={{ animation: 'cl-bounce 0.8s ease-in-out 0.15s infinite' }} />
            <span className="w-1.5 h-1.5 rounded-full bg-[#ccc77d]" style={{ animation: 'cl-bounce 0.8s ease-in-out 0.3s infinite' }} />
          </div>
        </div>
      </div>

      <style>{`
        @keyframes cl-spin { to { transform: rotate(360deg); } }
        @keyframes cl-bounce { 0%, 80%, 100% { opacity: 0.3; transform: translateY(0); } 40% { opacity: 1; transform: translateY(-4px); } }
      `}</style>
    </div>
  );
}
